import { Button } from "@/components/ui/button";
import { Pencil, Trash2 } from "lucide-react";
import { fmtMoney, statusTone } from "@/lib/format";
import InventoryRow from "@/components/inventory/InventoryRow";
import WatchThumb from "@/components/WatchThumb";

const COLUMNS = [
  { key: "excel_id", label: "ID" },
  { key: "_thumb", label: "" },
  { key: "brand", label: "Brand" },
  { key: "model", label: "Model / Notes" },
  { key: "caliber", label: "Caliber" },
  { key: "mfg", label: "Mvmnt MFG" },
  { key: "year_mfg", label: "Year" },
  { key: "movement_type", label: "Type" },
  { key: "bph", label: "BPH" },
  { key: "jewels", label: "Jewels" },
  { key: "shock", label: "Shock" },
  { key: "status", label: "Status" },
  { key: "bin_location", label: "Location" },
  { key: "all_in", label: "All In" },
  { key: "parts", label: "Parts" },
  { key: "total_cost", label: "Total" },
  { key: "media_count", label: "Pics" },
  { key: "sale_price", label: "Sale" },
  { key: "profit_loss", label: "P/L" },
];

function plClass(v) {
  if (v > 0) return "text-emerald-700";
  if (v < 0) return "text-rose-600";
  return "text-[#3A2A10]";
}

/** Compact card — shown on mobile in place of the 16-column table. */
function MobileCard({ w, onOpen, onEdit, onDelete }) {
  return (
    <div
      onClick={() => onOpen(w)}
      className="bg-[#F9F0D4] border border-[#8B6A1F]/35 p-4 active:bg-[#2B2214]/[0.06]"
      data-testid={`mobile-row-${w.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <WatchThumb watch={w} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="font-mono-data text-xs text-[#3A2A10]">#{w.excel_id ?? "—"}</span>
            <span className="text-base text-[#0A0604] truncate">{w.brand || "Unbranded"}</span>
          </div>
          <div className="text-sm text-[#3A2A10] truncate mt-0.5" title={w.model || ""}>
            {w.model || "—"}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-[#3A2A10] font-mono-data">
            {w.caliber && <span>{w.caliber}</span>}
            {w.mfg && <span>{w.mfg}</span>}
            {w.year_mfg && <span>{w.year_mfg}</span>}
            {w.bin_location && <span>Location {w.bin_location}</span>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {w.status && (
            <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 border rounded-sm whitespace-nowrap ${statusTone(w.status)}`}>
              {w.status}
            </span>
          )}
          <div className={`font-mono-data text-sm ${plClass(w.profit_loss)}`}>
            {fmtMoney(w.profit_loss)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-[#8B6A1F]/25 text-[10px] uppercase tracking-wider text-[#3A2A10]">
        <div>
          <div>All In</div>
          <div className="font-mono-data text-sm text-[#0A0604] normal-case tracking-normal">{fmtMoney(w.all_in)}</div>
        </div>
        <div>
          <div>Parts</div>
          <div className="font-mono-data text-sm text-[#0A0604] normal-case tracking-normal">{fmtMoney(w.parts)}</div>
        </div>
        <div>
          <div className="font-semibold">Total</div>
          <div className="font-mono-data text-sm text-[#0A0604] normal-case tracking-normal font-semibold">
            {fmtMoney((Number(w.all_in) || 0) + (Number(w.parts) || 0))}
          </div>
        </div>
        <div>
          <div>Sale</div>
          <div className="font-mono-data text-sm text-[#0A0604] normal-case tracking-normal">{fmtMoney(w.sale_price)}</div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-1 mt-3">
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(w); }}
          className="h-9 w-9 inline-flex items-center justify-center text-[#3A2A10] border border-[#8B6A1F]/35 rounded-sm"
          aria-label="Edit watch"
          data-testid={`mobile-edit-${w.id}`}
        >
          <Pencil size={15} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(w); }}
          className="h-9 w-9 inline-flex items-center justify-center text-rose-600 border border-[#8B6A1F]/35 rounded-sm"
          aria-label="Delete watch"
          data-testid={`mobile-delete-${w.id}`}
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

function TableHead({ sort, order, onSort, density }) {
  const compact = density === "compact";
  const headPad = compact ? "px-3 py-1.5" : "px-4 py-3";
  return (
    <thead>
      <tr className="border-b border-[#8B6A1F]/35">
        {COLUMNS.map((c) => (
          <th
            key={c.key}
            onClick={() => c.key !== "_thumb" && onSort(c.key)}
            className={`text-left ${headPad} text-[10px] uppercase tracking-[0.15em] text-[#3A2A10] font-normal select-none whitespace-nowrap ${
              c.key === "_thumb" ? `w-10 ${compact ? "px-1.5" : "px-2"}` : "cursor-pointer"
            }`}
            data-testid={`sort-${c.key}`}
          >
            {c.label}
            {c.key !== "_thumb" && sort === c.key && <span className="ml-1 text-[#3A2A10]">{order === "asc" ? "↑" : "↓"}</span>}
          </th>
        ))}
        <th className={`${headPad} w-20`}></th>
      </tr>
    </thead>
  );
}

function EmptyState() {
  return (
    <tr>
      <td colSpan={COLUMNS.length + 1} className="px-4 py-16 text-center text-[#3A2A10]" data-testid="empty-state">
        <div className="font-serif-display text-2xl text-[#3A2A10] mb-2">Nothing here yet</div>
        <div className="text-xs text-[#3A2A10]">Try clearing your filters, or add a new watch.</div>
      </td>
    </tr>
  );
}

export default function InventoryTable({
  items, loading, sort, order, onSort,
  onOpen, onEdit, onDelete, onArchive,
  skip, total, page, onFirst, onPrev, onNext, onLast,
  density = "comfortable",
  pageSize, onPageSizeChange,
}) {
  return (
    <>
      {/* Desktop: wide data table */}
      <div className="hidden md:block border border-[#8B6A1F]/35 bg-[#F9F0D4] overflow-x-auto" data-testid="inventory-table">
        <table className={`w-full ${density === "compact" ? "text-[13px]" : "text-sm"}`}>
          <TableHead sort={sort} order={order} onSort={onSort} density={density} />
          <tbody>
            {loading && (
              <tr><td colSpan={COLUMNS.length + 1} className="px-4 py-12 text-center text-[#3A2A10]">Loading inventory…</td></tr>
            )}
            {!loading && items.length === 0 && <EmptyState />}
            {!loading && items.map((w) => (
              <InventoryRow key={w.id} watch={w} density={density} onOpen={onOpen} onEdit={onEdit} onDelete={onDelete} onArchive={onArchive} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: compact card list */}
      <div className="md:hidden space-y-3" data-testid="inventory-card-list">
        {loading && (
          <div className="py-10 text-center text-[#3A2A10] text-sm">Loading inventory…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 px-4 py-12 text-center" data-testid="mobile-empty-state">
            <div className="font-serif-display text-xl text-[#3A2A10] mb-1">Nothing here yet</div>
            <div className="text-xs text-[#3A2A10]">Try clearing your filters, or add a new watch.</div>
          </div>
        )}
        {!loading && items.map((w) => (
          <MobileCard key={w.id} w={w} onOpen={onOpen} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>

      <div className="flex items-center justify-between mt-5 gap-3 flex-wrap">
        <div className="flex items-center gap-3 text-xs text-[#3A2A10]">
          <span className="font-mono-data">
            {total === 0 ? "0" : `${skip + 1}–${Math.min(skip + page, total)}`} / {total.toLocaleString()}
          </span>
          {onPageSizeChange && (
            <label className="hidden md:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider" data-testid="page-size-label">
              Per page
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="h-7 px-1.5 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-xs text-[#0A0604] font-mono-data focus:outline-none"
                data-testid="page-size-select"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onFirst}
            disabled={skip === 0}
            className="h-8 border-[#8B6A1F]/45 bg-transparent hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm"
            data-testid="page-first"
          >
            « First
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onPrev}
            disabled={skip === 0}
            className="h-8 border-[#8B6A1F]/45 bg-transparent hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm"
            data-testid="page-prev"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onNext}
            disabled={skip + page >= total}
            className="h-8 border-[#8B6A1F]/45 bg-transparent hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm"
            data-testid="page-next"
          >
            Next
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onLast}
            disabled={skip + page >= total}
            className="h-8 border-[#8B6A1F]/45 bg-transparent hover:bg-[#2B2214]/[0.14] hover:text-[#3A2A10] rounded-sm"
            data-testid="page-last"
          >
            Last »
          </Button>
        </div>
      </div>
    </>
  );
}
